import { ColDef } from 'ag-grid-community';
import { DeviceListAllFaultsResponse } from 'api/actions/device-list-all-faults/device-list-all-faults-response';
import { useApi } from 'api/api-context';
import ActionsColumn from 'components/tables/data/fault/columns/ActionsColumn';
import DescriptionColumn from 'components/tables/data/fault/columns/DescriptionColumn';
import DeviceIdColumn from 'components/tables/data/fault/columns/DeviceIdColumn';
import DeviceNameColumn from 'components/tables/data/fault/columns/DeviceNameColumn';
import DeviceSubtypeNameColumn from 'components/tables/data/fault/columns/DeviceSubtypeNameColumn';
import DeviceTypeNameColumn from 'components/tables/data/fault/columns/DeviceTypeNameColumn';
import FaultIdColumn from 'components/tables/data/fault/columns/FaultIdColumn';
import FaultNameColumn from 'components/tables/data/fault/columns/FaultNameColumn';
import FaultKindColumn from 'components/tables/data/fault/columns/FaultKindColumn';
import FaultDateColumn from 'components/tables/data/fault/columns/FaultDateColumn';
import RevisionSafetyPointColumn from 'components/tables/data/fault/columns/RevisionSafetyPointColumn';
import FaultSeverityNameColumn from 'components/tables/data/fault/columns/FaultSeverityNameColumn';
import ExternalIdColumn from 'components/tables/data/fault/columns/ExternalIdColumn';
import FixedAtColumn from 'components/tables/data/fault/columns/FixedAtColumn';
import RecordedByNameColumn from 'components/tables/data/fault/columns/RecordedByNameColumn';
import FaultAuditUserSelectFilter from 'components/tables/filters/FaultAuditUserSelectFilter';
import FixedByNameColumn from 'components/tables/data/fault/columns/FixedByNameColumn';
import FixedNoteColumn from 'components/tables/data/fault/columns/FixedNoteColumn';
import MeasurementContextColumn from 'components/tables/data/fault/columns/MeasurementContextColumn';
import MoreInfoColumn from 'components/tables/data/fault/columns/MoreInfoColumn';
import OrganizationNameColumn from 'components/tables/data/fault/columns/OrganizationNameColumn';
import RemoteSourceColumn from 'components/tables/data/fault/columns/RemoteSourceColumn';
import { FaultRow } from 'components/tables/data/fault/types';
import DataTable, { IDataTablePublic } from 'components/tables/DataTable';
import { DataTableExport } from 'components/tables/ExportModal';
import { getEvidenceFieldValue } from 'utils/evidence-field';
import useObjectColumn from 'components/tables/hierarchy/ObjectColumn';
import DeviceSubtypeEqualsSelectFilter from 'components/tables/filters/device-subtype/DeviceSubtypeEqualsSelectFilter';
import DeviceSelectFilter from 'components/tables/filters/DeviceSelectFilter';
import DeviceTypeSelectFilter from 'components/tables/filters/DeviceTypeSelectFilter';
import DateFilter, { DateColumnFilter } from 'components/tables/filters/DateFilter';
import FaultKindSelectFilter from 'components/tables/filters/FaultKindSelectFilter';
import FaultSeverityFilter from 'components/tables/filters/FaultSeveritySelectFilter';
import RevisionSafetyPointSelectFilter from 'components/tables/filters/RevisionSafetyPointSelectFilter';
import OrganizationMultiSelectFilter, {
  OrganizationMultiColumnFilter,
} from 'components/tables/filters/OrganizationMultiSelectFilter';
import RemoteSourceSelectFilter from 'components/tables/filters/RemoteSourceSelectFilter';
import SimpleValueFormatter from 'components/tables/SimpleValueFormatter';
import { useCounter } from '@mantine/hooks';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FEATURE_TOGGLE_EVIDENCE_FIELDS_IN_DEVICE_TABLE, FEATURE_TOGGLE_REMOTE_SOURCES } from 'env';
import { getFaultKindPretty } from 'components/tables/data/fault/kind';
import { translateSafetyPoint } from 'model/RevisionSafetyPoint';
import { getResponsiveActionsColumnWidth, useResponsiveDesign } from 'hooks/use-responsive-design';
import useOrganizationModules from 'features/organization-modules/use-organization-modules';

type EvidenceField = NonNullable<NonNullable<DeviceListAllFaultsResponse[number]['device']>['evidenceFields']>[number];

interface FaultTableProps {
  suppressSaveProfiles?: boolean;
  suppressProfileChangedNotification?: boolean;
  initialFilters?: Record<string, any>;
}

/**
 * Table which displays device faults.
 */
const FaultTable = forwardRef<IDataTablePublic, FaultTableProps>(
  ({ initialFilters, suppressProfileChangedNotification, suppressSaveProfiles }, ref) => {
    const { getAction } = useApi();
    const { revisions: revisionsModuleEnabled, forms: formsModuleEnabled } = useOrganizationModules();
    const { tableActionMode } = useResponsiveDesign();
    const [evidenceFieldsColumns, setEvidenceFieldsColumns] = useState<ColDef[]>([]);
    const inventoryRequest = useRef(0);
    const [faults, setFaults] = useState<FaultRow[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [refreshToken, { increment: incrementRefreshToken }] = useCounter(0);
    const objectColumn = useObjectColumn({
      breadcrumbField: 'department.breadcrumbs',
      organizationFilterKey: 'organization.organizationId',
      permissionSlug: 'browse-devices',
      legacyColumnIds: ['department.departmentName'],
    });

    const fetchFaults = useCallback(async () => {
      const request = ++inventoryRequest.current;
      const action = getAction('DeviceListAllFaults');
      const faults = await action();
      if (request !== inventoryRequest.current) return;
      const evidenceFieldMap = new Map<string, EvidenceField>();

      const augmentedFaults = faults
        .filter(({ revision }) => {
          if (!revision) return true;
          return revision.kind === 'revision' ? revisionsModuleEnabled : formsModuleEnabled;
        })
        .map((fault) => {
          const evidenceFields = fault.device?.evidenceFields?.reduce((acc, field) => {
            if (!field.fieldName || !field.evidenceFieldId) {
              return acc;
            }

            const key = String(field.evidenceFieldId);
            const value = getEvidenceFieldValue(field) ?? '';

            evidenceFieldMap.set(key, field);
            acc[key] = value;

            return acc;
          }, {} as Record<string, string | number | boolean>);

          return {
            ...fault,
            kindPretty: getFaultKindPretty(fault.kind),
            safetyPoint: fault.revision?.safetyPoint,
            safetyPointLabel: translateSafetyPoint(fault.revision?.safetyPoint, fault.revision?.safetyPointLabel),
            safetyPointPretty: translateSafetyPoint(fault.revision?.safetyPoint, fault.revision?.safetyPointLabel),
            revisionId: fault.revision?.revisionId,
            revisionStatusText: fault.revision?.revisionStatus?.revisionStatusName ?? '',
            measurementContextText: fault.measurementContext
              ? `Riadok č.${fault.measurementContext.rowNumber} V tabuľke ${fault.measurementContext.tableName}`
              : '',
            measurementContextTableName: fault.measurementContext?.tableName ?? '',
            measurementContextRowNumber: fault.measurementContext?.rowNumber ?? '',
            isFixedText: fault.fixed && fault.fixed.fixedAt !== undefined ? 'fixed' : 'not-fixed',
            isFixed: fault.fixed && fault.fixed.fixedAt !== undefined,
            department: fault.device!.department,
            organization: fault.device!.organization,
            deviceType: fault.device!.deviceType,
            deviceSubtype: fault.device!.deviceSubtype,
            deviceId: fault.device!.deviceId,
            deviceName: fault.device!.deviceName,
            evidenceFields,
            fixed: {
              fixedAt: fault.fixed?.fixedAt,
              fixedNote: fault.fixed?.fixedNote,
              recordedBy: fault.fixed
                ? {
                    ...(fault.fixed.recordedBy ?? fault.fixed.fixedBy),
                    fullName:
                      (fault.fixed.recordedBy ?? fault.fixed.fixedBy).fullName ??
                      `${(fault.fixed.recordedBy ?? fault.fixed.fixedBy).firstName} ${
                        (fault.fixed.recordedBy ?? fault.fixed.fixedBy).lastName
                      }`,
                  }
                : undefined,
              fixedBy: {
                userId: fault.fixed?.fixedBy.userId,
                firstName: fault.fixed?.fixedBy.firstName,
                lastName: fault.fixed?.fixedBy.lastName,
                fullName:
                  fault.fixed && fault.fixed.fixedAt !== undefined
                    ? `${fault.fixed?.fixedBy.firstName} ${fault.fixed?.fixedBy.lastName}`
                    : undefined,
              },
            },
          };
        });

      const evidenceFieldsColumns = Array.from(evidenceFieldMap).map(
        ([key, { fieldName: name, precision = 0 }]) =>
          ({
            field: `evidenceFields.${key}`,
            headerName: name,
            minWidth: 200,
            sortable: true,
            unSortIcon: true,
            filter: true,
            floatingFilter: true,
            hide: !FEATURE_TOGGLE_EVIDENCE_FIELDS_IN_DEVICE_TABLE,
            filterValueGetter: ({ data: { evidenceFields } }: { data: FaultRow }) => (evidenceFields as any)?.[key],
            cellRenderer: ({ data: { evidenceFields } }: { data: FaultRow }) => {
              const value = (evidenceFields as any)?.[key];
              return <SimpleValueFormatter value={value} precision={precision} />;
            },
            icons: {
              filter: () =>
                '<div title="Pokročilé filtrovanie"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-filter-spark"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M15 12.5v-.5l4.414 -4.414a2 2 0 0 0 .586 -1.414v-2.172h-16v2.227c0 .497 .185 .977 .52 1.345l4.48 4.928v8.5l2 -.667" /><path d="M18.5 22a4.75 4.75 0 0 1 3.5 -3.5a4.75 4.75 0 0 1 -3.5 -3.5a4.75 4.75 0 0 1 -3.5 3.5a4.75 4.75 0 0 1 3.5 3.5" /></svg></div>',
            },
          } satisfies ColDef)
      );

      setEvidenceFieldsColumns(evidenceFieldsColumns);
      setFaults(augmentedFaults as FaultRow[]);
      setLoading(false);
      incrementRefreshToken();
    }, [formsModuleEnabled, getAction, revisionsModuleEnabled]);

    const action = useCallback(() => Promise.resolve(faults), [faults]);

    useEffect(() => {
      fetchFaults();
      return () => {
        inventoryRequest.current += 1;
      };
    }, [fetchFaults]);

    const columns = useMemo<ColDef[]>(
      () => [
        {
          field: 'faultId',
          headerName: 'ID',
          sortable: true,
          unSortIcon: true,
          width: 76,
          minWidth: 76,
          maxWidth: 76,
          resizable: false,
          cellRenderer: FaultIdColumn,
        },
        {
          field: 'faultName',
          headerName: 'Názov nedostatku',
          minWidth: 400,
          sortable: true,
          unSortIcon: true,
          cellRenderer: FaultNameColumn,
        },
        {
          field: 'kind',
          headerName: 'Pôvod',
          hide: true,
          filter: true,
        },
        {
          field: 'revisionId',
          headerName: 'ID Pôvodu',
          hide: true,
        },
        {
          field: 'kindPretty',
          headerName: 'Pôvod',
          minWidth: 220,
          filter: true,
          floatingFilter: true,
          floatingFilterComponent: FaultKindSelectFilter,
          sortable: true,
          unSortIcon: true,
          cellRenderer: FaultKindColumn,
        },
        {
          field: 'safetyPoint',
          hide: true,
          filter: true,
        },
        {
          field: 'safetyPointLabel',
          hide: true,
          filter: true,
        },
        {
          field: 'safetyPointPretty',
          headerName: 'Stav',
          minWidth: 180,
          filter: true,
          floatingFilter: true,
          floatingFilterComponent: RevisionSafetyPointSelectFilter,
          sortable: true,
          unSortIcon: true,
          cellRenderer: RevisionSafetyPointColumn,
        },
        {
          field: 'revision.finishedAt',
          headerName: 'Dátum nedostatku',
          filter: DateColumnFilter,
          floatingFilter: true,
          floatingFilterComponent: DateFilter.configure({ filterKey: 'revision.finishedAt' }),
          minWidth: 200,
          sortable: true,
          unSortIcon: true,
          cellRenderer: FaultDateColumn,
        },
        {
          field: 'measurementContextText',
          headerName: 'Meranie',
          minWidth: 260,
          filter: true,
          sortable: true,
          unSortIcon: true,
          cellRenderer: MeasurementContextColumn,
        },
        {
          field: 'measurementContextTableName',
          headerName: 'Meranie - Názov tabuľky',
          hide: true,
        },
        {
          field: 'measurementContextRowNumber',
          headerName: 'Meranie - Číslo riadku',
          hide: true,
        },
        {
          field: 'description',
          headerName: 'Popis nedostatku',
          minWidth: 250,
          cellRenderer: DescriptionColumn,
        },
        {
          field: 'moreInfo',
          headerName: 'Dodatok',
          minWidth: 250,
          cellRenderer: MoreInfoColumn,
        },
        {
          field: 'deviceId',
          headerName: 'ID zariadenia',
          filter: true,
          minWidth: 140,
          sortable: true,
          unSortIcon: true,
          cellRenderer: DeviceIdColumn,
        },
        {
          field: 'deviceName',
          headerName: 'Názov zariadenia',
          minWidth: 350,
          filter: true,
          floatingFilter: true,
          floatingFilterComponent: DeviceSelectFilter,
          sortable: true,
          unSortIcon: true,
          cellRenderer: DeviceNameColumn,
        },
        {
          field: 'faultSeverity.faultSeverityId',
          headerName: 'ID závažnosti',
          hide: true,
          filter: true,
        },
        {
          field: 'faultSeverity.faultSeverityName',
          headerName: 'Závažnosť',
          minWidth: 320,
          filter: true,
          floatingFilter: true,
          floatingFilterComponent: FaultSeverityFilter,
          cellRenderer: FaultSeverityNameColumn,
        },
        {
          field: 'isFixedText',
          hide: true,
          filter: true,
        },
        {
          field: 'fixed.fixedAt',
          headerName: 'Dátum riešenia',
          filter: DateColumnFilter,
          floatingFilter: true,
          floatingFilterComponent: DateFilter.configure({ filterKey: 'fixed.fixedAt' }),
          minWidth: 200,
          sortable: true,
          unSortIcon: true,
          cellRenderer: FixedAtColumn,
        },
        {
          field: 'fixed.fixedNote',
          headerName: 'Popis riešenia',
          minWidth: 200,
          cellRenderer: FixedNoteColumn,
        },
        {
          field: 'fixed.fixedBy.userId',
          headerName: 'ID riešiteľa nedostatku',
          hide: true,
          filter: true,
        },
        {
          field: 'fixed.fixedBy.fullName',
          headerName: 'Vyriešil',
          minWidth: 200,
          sortable: true,
          unSortIcon: true,
          filter: true,
          floatingFilter: true,
          floatingFilterComponent: FaultAuditUserSelectFilter.configure({ reference: 'fixedBy' }),
          getQuickFilterText: ({ data }) => data?.fixed?.fixedBy.fullName ?? '',
          cellRenderer: FixedByNameColumn,
        },
        {
          field: 'fixed.recordedBy.userId',
          headerName: 'ID používateľa, ktorý zaznamenal riešenie',
          hide: true,
          filter: true,
        },
        {
          field: 'fixed.recordedBy.fullName',
          headerName: 'Zaznamenal riešenie',
          minWidth: 220,
          sortable: true,
          unSortIcon: true,
          filter: true,
          floatingFilter: true,
          floatingFilterComponent: FaultAuditUserSelectFilter.configure({ reference: 'recordedBy' }),
          getQuickFilterText: ({ data }) => data?.fixed?.recordedBy?.fullName ?? '',
          cellRenderer: RecordedByNameColumn,
        },
        {
          field: 'organization.organizationId',
          headerName: 'ID organizácie',
          hide: true,
          filter: OrganizationMultiColumnFilter,
        },
        {
          field: 'organization.organizationName',
          headerName: 'Organizácia',
          minWidth: 275,
          filter: true,
          floatingFilter: true,
          floatingFilterComponent: OrganizationMultiSelectFilter.configure({ permissionSlug: 'browse-devices' }),
          sortable: true,
          unSortIcon: true,
          cellRenderer: OrganizationNameColumn,
        },
        ...objectColumn.columns,
        {
          field: 'deviceType.deviceTypeId',
          hide: true,
          filter: true,
        },
        {
          field: 'deviceType.deviceTypeName',
          headerName: 'Zariadenie',
          minWidth: 220,
          filter: true,
          floatingFilter: true,
          floatingFilterComponent: DeviceTypeSelectFilter,
          sortable: true,
          unSortIcon: true,
          cellRenderer: DeviceTypeNameColumn,
        },
        {
          field: 'deviceSubtype.deviceTypeId',
          hide: true,
          filter: true,
        },
        {
          field: 'deviceSubtype.deviceTypeName',
          headerName: 'Typ zariadenia',
          minWidth: 250,
          filter: true,
          floatingFilter: true,
          floatingFilterComponent: DeviceSubtypeEqualsSelectFilter,
          sortable: true,
          unSortIcon: true,
          cellRenderer: DeviceSubtypeNameColumn,
        },
        ...(FEATURE_TOGGLE_REMOTE_SOURCES
          ? [
              {
                field: 'remoteSource.remoteSourceId',
                headerName: 'ID externého zdroja',
                hide: true,
                filter: true,
              },
              {
                field: 'remoteSource.name',
                headerName: 'Externý zdroj',
                minWidth: 200,
                filter: true,
                floatingFilter: true,
                floatingFilterComponent: RemoteSourceSelectFilter,
                sortable: true,
                unSortIcon: true,
                cellRenderer: RemoteSourceColumn,
              },
            ]
          : []),
        {
          field: 'externalId',
          headerName: 'Externé ID',
          minWidth: 160,
          cellRenderer: ExternalIdColumn,
        },
        ...evidenceFieldsColumns,
        {
          field: '_actions',
          headerName: '',
          pinned: 'right',
          width: getResponsiveActionsColumnWidth(194, tableActionMode),
          minWidth: getResponsiveActionsColumnWidth(194, tableActionMode),
          maxWidth: getResponsiveActionsColumnWidth(194, tableActionMode),
          cellRenderer: ActionsColumn,
        },
      ],
      [evidenceFieldsColumns, objectColumn.columns, tableActionMode]
    );

    const exportColumns = useMemo(
      () => [
        'faultId',
        'faultName',
        'kindPretty',
        'revisionId',
        'revision.finishedAt',
        'safetyPointPretty',
        'measurementContextText',
        'measurementContextTableName',
        'measurementContextRowNumber',
        'description',
        'moreInfo',
        'deviceId',
        'deviceName',
        'faultSeverity.faultSeverityName',
        'fixed.fixedAt',
        'fixed.fixedNote',
        'fixed.fixedBy.fullName',
        'fixed.recordedBy.fullName',
        'organization.organizationId',
        'organization.organizationName',
        ...objectColumn.columnKeys,
        'deviceType.deviceTypeName',
        'deviceSubtype.deviceTypeName',
        'remoteSource.remoteSourceId',
        'remoteSource.name',
        'externalId',
        ...evidenceFieldsColumns.map(({ field }) => field ?? '').filter(Boolean),
      ],
      [evidenceFieldsColumns, objectColumn.columnKeys]
    );

    const dataExport: DataTableExport<FaultRow> = useMemo(
      () => ({
        modalTitle: 'Exportovať nedostatky zariadení',
        fileName: 'nedostatky-zariadeni.xlsx',
        columnKeys: exportColumns,
        columnReplacements: objectColumn.columnReplacements,
        ready: objectColumn.exportReady,
        error: objectColumn.exportError,
        initialSelectedColumns: (rows) =>
          exportColumns.filter((column) => {
            if (column.startsWith('evidenceFields.')) {
              const fieldKey = column.replace('evidenceFields.', '');

              return rows.some((row) => {
                const value = (row.evidenceFields as any)?.[fieldKey];
                return value !== undefined && value !== null && value !== '';
              });
            }

            return true;
          }),
      }),
      [exportColumns, objectColumn.columnReplacements, objectColumn.exportError, objectColumn.exportReady]
    );

    if (loading) {
      return null;
    }

    const tableId = loading ? 'faults-loading' : 'faults';

    return (
      <DataTable
        key={`${tableId}-${refreshToken}`}
        ref={ref}
        tableId={tableId}
        title="Nedostatky zariadení"
        hideToggleDiscarded
        columns={columns}
        action={action}
        initialFilters={initialFilters}
        suppressSaveProfiles={suppressSaveProfiles}
        suppressProfileChangedNotification={suppressProfileChangedNotification}
        dataExport={dataExport}
        profileColumnAliases={objectColumn.profileColumnAliases}
        profileMigrationReady={objectColumn.profileMigrationReady}
      />
    );
  }
);

export default FaultTable;
